import { ThemeEvents, VariantUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** Quantity currently selected (e.g. jar selector), used to show the line total. */
  #quantity = 1;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.updateQuantity);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.updateQuantity);
  }

  /**
   * Multiplies the displayed price by the selected quantity.
   * Only applies when the block exposes a unit price (see blocks/product-price-weight.liquid).
   * @param {QuantitySelectorUpdateEvent} event
   */
  updateQuantity = (event) => {
    // Cart line quantity changes are not for the product page price.
    if (event.detail.cartLine !== undefined) return;

    this.#quantity = event.detail.quantity;
    this.#renderQuantityPrice();
  };

  #renderQuantityPrice() {
    const { unitPrice, unitComparePrice, moneyFormat, currency } = this.dataset;
    if (!unitPrice || !moneyFormat || !currency) return;

    const quantity = Math.max(1, this.#quantity);
    const price = formatMoney(Number(unitPrice) * quantity, moneyFormat, currency);
    const comparePrice = formatMoney(Number(unitComparePrice || 0) * quantity, moneyFormat, currency);

    for (const el of this.querySelectorAll('[ref="priceContainer"] .price')) el.textContent = price;
    if (Number(unitComparePrice) > 0) {
      for (const el of this.querySelectorAll('[ref="priceContainer"] .compare-at-price')) el.textContent = comparePrice;
    }
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Keep the quantity total in sync with the new variant's unit price
    if (newProductPrice instanceof HTMLElement && newProductPrice.dataset.unitPrice) {
      this.dataset.unitPrice = newProductPrice.dataset.unitPrice;
      this.dataset.unitComparePrice = newProductPrice.dataset.unitComparePrice;
      this.#renderQuantityPrice();
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
